/**
 * JobBomPanel — read-only BOM visibility for a job.
 *
 * Source rows: bound JobSpec snapshot BOM (canonical).
 * Overlays:    PO line items (from OrderDraft.pushedPoIds → PurchaseOrder),
 *              MaterialKit item status.
 * Banner:      Material Alignment Gate verdict.
 *
 * Constraints (locked):
 *   • Read-only. No mutation, no actions.
 *   • Spec BOM is the row source — PO/Kit are joined per SKU as overlays.
 *     Never merge them into one ambiguous list.
 *   • Vendor only shown when resolvable from PO/Draft. Never invent on spec.
 *   • No finish/color parsing in v1.
 *   • No pricing/cost surfaced.
 */

import { useEffect, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, CheckCircle2, AlertTriangle, Circle } from "lucide-react";
import type { BoundJobSpec } from "@/data/jobSpec";
import { resolveBoundSpec, formatSpecRef } from "@/data/jobSpec";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { useVendorPO, type PurchaseOrder } from "@/data/VendorPOContext";
import { useWarehouseInventory, type JobAllocation } from "@/data/WarehouseInventoryContext";
import { getAllocationCoverage, type AllocationCoverageRow } from "@/data/inventory/allocationValidation";
import { assertMaterialAlignment, summarizeAlignment } from "@/data/materialAlignmentGate";
import { WarehouseService } from "@/services/warehouse.service";
import { createKitFromSpec, type MaterialKit } from "@/data/materialKit";
import { migrationBypasses } from "@/data/import/migrationVisibilityGuard";
import type { Job, MaterialLineItem } from "@/data/types";

interface JobBomPanelProps {
  jobId: string;
  bound?: BoundJobSpec;
  /** Pass the real Supabase job object to avoid falling back to AppDataContext seed data. */
  job?: Job;
  /** Job-scoped overlay data for routes that intentionally defer global providers. */
  materialKit?: MaterialKit;
  purchaseOrders?: PurchaseOrder[];
  jobAllocations?: JobAllocation[];
  /** Actual rows from the job_materials table — used as fallback when no bound spec exists. */
  dbMaterials?: MaterialLineItem[];
}

type PoStatusOverlay = "none" | "draft" | "pushed";
type KitStatusOverlay = "not_generated" | "pending" | "confirmed" | "missing";

export function JobBomPanel({
  jobId,
  bound,
  job: propJob,
  materialKit: propMaterialKit,
  purchaseOrders: propPurchaseOrders,
  jobAllocations: propJobAllocations,
  dbMaterials,
}: JobBomPanelProps) {
  const { jobs, materialKits } = useAppData();
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { drafts } = useOrderDraft();
  const { purchaseOrders } = useVendorPO();
  const { jobAllocations } = useWarehouseInventory();
  const effectivePurchaseOrders = propPurchaseOrders ?? purchaseOrders;
  const effectiveJobAllocations = propJobAllocations ?? jobAllocations;

  // Prefer the explicitly-passed real job over AppDataContext seed lookup.
  const job = useMemo(
    () => propJob ?? jobs.find((j) => j.id === jobId),
    [propJob, jobs, jobId],
  );
  const spec = useMemo(() => resolveBoundSpec(bound), [bound]);
  const draft = drafts[jobId];
  const kitFromContext = propMaterialKit ?? materialKits.find((k) => k.jobId === jobId);
  // When no AppDataContext kit exists but the spec is bound, auto-synthesize one
  // from the spec BOM so the alignment gate can compute kitHash.
  // Items are initialized as "pending" — warehouse verification is a separate step.
  const kit = useMemo(() => {
    if (kitFromContext) return kitFromContext;
    if (!spec || spec.billOfMaterials.length === 0) return undefined;
    return createKitFromSpec(jobId, spec);
  }, [kitFromContext, spec, jobId]);

  // Phase 4A.5 — per-SKU allocation coverage (read-only overlay).
  // When no bound spec, build coverage directly from dbMaterials rows (which carry
  // quantityRequired + quantityAllocated from the job_materials table).
  const coverageBySku = useMemo(() => {
    const usingDb = (!spec || spec.billOfMaterials.length === 0) && (dbMaterials ?? []).length > 0;
    if (usingDb) {
      return new Map(
        (dbMaterials ?? []).map((m) => {
          const required = m.quantityRequired;
          const allocated = m.quantityAllocated;
          const shortBy = Math.max(required - allocated, 0);
          return [m.sku, { required, allocated, shortBy, isShort: shortBy > 0 }] as const;
        }),
      );
    }
    if (!job) return new Map<string, { required: number; allocated: number; shortBy: number; isShort: boolean }>();
    const rows = getAllocationCoverage(job, effectiveJobAllocations);
    return new Map(rows.map((r) => [r.sku, r]));
  }, [spec, dbMaterials, job, effectiveJobAllocations]);
  const autoRepairAttemptedRef = useRef<Set<string>>(new Set());

  // PO line aggregation across pushed POs for this job.
  const poByJob = useMemo(() => {
    if (draft?.status === "pushed" && draft.pushedPoIds?.length) {
      const ids = new Set(draft.pushedPoIds);
      return effectivePurchaseOrders.filter((po) => ids.has(po.poId));
    }
    return effectivePurchaseOrders.filter((po) => po.jobId === jobId);
  }, [effectivePurchaseOrders, draft, jobId]);

  const poBySku = useMemo(() => {
    const m = new Map<string, { quantity: number; vendorName: string }>();
    for (const po of poByJob) {
      for (const li of po.items) {
        const prev = m.get(li.sku);
        m.set(li.sku, {
          quantity: (prev?.quantity ?? 0) + li.quantity,
          // First vendor wins; if multiple vendors carry the same SKU, fall back to label.
          vendorName: prev && prev.vendorName !== po.vendorName ? "Multiple" : po.vendorName,
        });
      }
    }
    return m;
  }, [poByJob]);

  // Some specs can contain repeated SKU rows (e.g. split lines/add-ons).
  // PO lines are aggregated per SKU, so PO parity should be compared against
  // total required per SKU, not each individual row quantity.
  const specQtyBySku = useMemo(() => {
    const totals = new Map<string, number>();
    for (const b of spec?.billOfMaterials ?? []) {
      totals.set(b.sku, (totals.get(b.sku) ?? 0) + b.quantity);
    }
    return totals;
  }, [spec]);

  const poStatus: PoStatusOverlay = useMemo(() => {
    if (poByJob.length > 0) return "pushed";
    if (draft && draft.status === "prepared") return "draft";
    return "none";
  }, [poByJob, draft]);

  const verdict = useMemo(() => {
    if (!job) {
      return { ok: false, reasons: ["Job not found."], missing: [] as ("spec" | "po" | "kit")[] };
    }
    return assertMaterialAlignment(job, {
      kit,
      orderDrafts: drafts,
      purchaseOrders: effectivePurchaseOrders,
    });
  }, [job, kit, drafts, effectivePurchaseOrders]);
  const mismatchFlags = useMemo(() => {
    const specHash = verdict.hashes?.specHash;
    const poHash = verdict.hashes?.poHash;
    const kitHash = verdict.hashes?.kitHash;
    if (!specHash || !poHash || !kitHash) {
      return { specVsPo: false, poVsKit: false, specVsKit: false };
    }
    return {
      specVsPo: specHash !== poHash,
      poVsKit: poHash !== kitHash,
      specVsKit: specHash !== kitHash,
    };
  }, [verdict.hashes]);

  const usingDbMaterials = (!spec || spec.billOfMaterials.length === 0) && (dbMaterials ?? []).length > 0;

  const rows = useMemo(() => {
    const bomSource = usingDbMaterials
      ? (dbMaterials ?? []).map((m) => ({ sku: m.sku, name: m.itemName, quantity: m.quantityRequired, unit: undefined as string | undefined }))
      : (spec?.billOfMaterials ?? []).map((b) => ({ sku: b.sku, name: b.name, quantity: b.quantity, unit: b.unit }));
    return bomSource.map((b) => {
      const po = poBySku.get(b.sku);
      const kitItem = kit?.items.find((i) => i.sku === b.sku);
      const kitStatus: KitStatusOverlay = !kit
        ? "not_generated"
        : kitItem
          ? (kitItem.status as KitStatusOverlay)
          : "not_generated";
      return {
        sku: b.sku,
        name: b.name,
        quantity: b.quantity,
        unit: b.unit,
        poQty: po?.quantity,
        vendor: po?.vendorName,
        kitStatus,
      };
    });
  }, [usingDbMaterials, dbMaterials, spec, poBySku, kit]);

  const hasAllocationShortage = rows.some((r) => (coverageBySku.get(r.sku)?.isShort ?? false));
  const shouldAttemptAutoRepair =
    !!job &&
    poStatus === "pushed" &&
    hasAllocationShortage &&
    !autoRepairAttemptedRef.current.has(jobId);

  useEffect(() => {
    if (!shouldAttemptAutoRepair) return;
    autoRepairAttemptedRef.current.add(jobId);
    void WarehouseService.reconcileJobAllocationsFromReceiving(jobId);
  }, [jobId, shouldAttemptAutoRepair]);

  if (!job) return null;

  // No bound spec and no DB materials — show explicit empty state.
  if ((!spec || spec.billOfMaterials.length === 0) && (dbMaterials ?? []).length === 0) {
    const isBypassed = migrationBypasses(job, "ordering");
    return (
      <Card className="shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
              <Package className="h-4 w-4" /> Materials / BOM
            </h3>
            {isBypassed ? (
              <Badge variant="outline" className="text-[10px] text-warning border-warning/30">
                Ordering bypassed
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-warning border-warning/30">
                Spec not bound
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {isBypassed
              ? "This job was migrated from an external system. Ordering is managed by the source system — no spec binding or BOM alignment is required."
              : "No JobSpec is bound to this job, so there is no canonical Bill of Materials to display. Bind a spec to enable BOM visibility, ordering alignment, and kit verification."}
          </p>
        </CardContent>
      </Card>
    );
  }

  const banner = bannerFor(verdict.ok, verdict.missing, summarizeAlignment(verdict));
  const canViewHashDebug = hasPerm("admin_view");

  return (
    <Card className="shadow-none">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
              <Package className="h-4 w-4" /> Materials / BOM
            </h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Source:{" "}
              <span className="font-mono">
                {usingDbMaterials ? "job_materials" : formatSpecRef({ specId: spec!.specId, versionNumber: spec!.versionNumber })}
              </span>
              {" · "}{rows.length} item{rows.length === 1 ? "" : "s"}
            </p>
          </div>
          <Badge variant="outline" className={`text-[10px] gap-1 ${banner.badgeClass}`}>
            {banner.icon}
            {banner.label}
          </Badge>
        </div>

        {/* Alignment banner — verbose copy */}
        <div className={`rounded-md border px-3 py-2 text-[11px] ${banner.bannerClass}`}>
          {banner.detail}
          {!verdict.ok && verdict.missing.length === 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
              {mismatchFlags.specVsPo && (
                <span className="px-1.5 py-0.5 rounded border border-warning/40 bg-warning/10">
                  Spec != PO
                </span>
              )}
              {mismatchFlags.poVsKit && (
                <span className="px-1.5 py-0.5 rounded border border-warning/40 bg-warning/10">
                  PO != Kit
                </span>
              )}
              {mismatchFlags.specVsKit && (
                <span className="px-1.5 py-0.5 rounded border border-warning/40 bg-warning/10">
                  Spec != Kit
                </span>
              )}
            </div>
          )}
        </div>
        {canViewHashDebug && (
          <details className="rounded-md border border-border/70 bg-muted/20 px-3 py-2">
            <summary className="cursor-pointer select-none text-[10px] font-semibold text-muted-foreground">
              Alignment debug hashes
            </summary>
            <div className="mt-2 grid gap-1 text-[10px]">
              <div className="flex items-center gap-2">
                <span className="w-14 text-muted-foreground uppercase tracking-wider">Spec</span>
                <code className="font-mono text-foreground break-all">{verdict.hashes?.specHash ?? "—"}</code>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-14 text-muted-foreground uppercase tracking-wider">PO</span>
                <code className="font-mono text-foreground break-all">{verdict.hashes?.poHash ?? "—"}</code>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-14 text-muted-foreground uppercase tracking-wider">Kit</span>
                <code className="font-mono text-foreground break-all">{verdict.hashes?.kitHash ?? "—"}</code>
              </div>
            </div>
          </details>
        )}

        {/* BOM table */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-semibold text-muted-foreground text-[10px] uppercase tracking-wider">SKU</th>
                <th className="px-2 py-1.5 font-semibold text-muted-foreground text-[10px] uppercase tracking-wider">Item</th>
                <th className="px-2 py-1.5 font-semibold text-muted-foreground text-[10px] uppercase tracking-wider text-right">Qty</th>
                <th className="px-2 py-1.5 font-semibold text-muted-foreground text-[10px] uppercase tracking-wider">Vendor</th>
                <th className="px-2 py-1.5 font-semibold text-muted-foreground text-[10px] uppercase tracking-wider">PO</th>
                <th className="px-2 py-1.5 font-semibold text-muted-foreground text-[10px] uppercase tracking-wider">Fulfillment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`${r.sku}-${idx}`} className="border-t hover:bg-muted/20">
                  <td className="px-2 py-1.5 font-mono text-[10px]">{r.sku}</td>
                  <td className="px-2 py-1.5">{r.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.quantity}{r.unit ? ` ${r.unit}` : ""}
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">{r.vendor ?? "—"}</td>
                  <td className="px-2 py-1.5">
                    <PoCell
                      status={poStatus}
                      hasPoLine={r.poQty != null}
                      poQty={r.poQty}
                      specQty={specQtyBySku.get(r.sku) ?? r.quantity}
                      coverage={coverageBySku.get(r.sku)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <FulfillmentCell
                      kitStatus={r.kitStatus}
                      coverage={coverageBySku.get(r.sku)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[10px] text-muted-foreground">
          Read-only view. Spec BOM is canonical; PO and Fulfillment columns are overlays sourced
          from pushed Purchase Orders, the Material Kit, and warehouse allocations.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Subcomponents ───────────────────────────────────────────────────────

function PoCell({
  status,
  hasPoLine,
  poQty,
  specQty,
  coverage,
}: {
  status: PoStatusOverlay;
  hasPoLine: boolean;
  poQty?: number;
  specQty: number;
  coverage?: AllocationCoverageRow;
}) {
  // Warehouse has fully allocated this item — material is in inventory regardless of PO path
  if (coverage && !coverage.isShort) {
    return <span className="text-[10px] text-success">Inventory</span>;
  }
  if (status === "none") {
    return <span className="text-[10px] text-muted-foreground">none</span>;
  }
  if (status === "draft") {
    return <span className="text-[10px] text-info">draft</span>;
  }
  // pushed
  if (!hasPoLine) {
    return <span className="text-[10px] text-warning">pushed · missing on PO</span>;
  }
  if (poQty !== specQty) {
    return (
      <span className="text-[10px] text-warning">
        pushed · qty {poQty} vs {specQty}
      </span>
    );
  }
  return <span className="text-[10px] text-success">pushed</span>;
}

function KitCell({ status }: { status: KitStatusOverlay }) {
  switch (status) {
    case "not_generated":
      return <span className="text-[10px] text-muted-foreground">not generated</span>;
    case "pending":
      return <span className="text-[10px] text-info">pending</span>;
    case "confirmed":
      return <span className="text-[10px] text-success">confirmed</span>;
    case "missing":
      return <span className="text-[10px] text-destructive">missing</span>;
  }
}

/**
 * FulfillmentCell — merged Kit + Allocation overlay (Phase 4A.5).
 * Stacks two thin lines: kit verification status, and allocation coverage.
 * Allocation is read-only; shortage shown as "Short by N · a/r alloc".
 */
function FulfillmentCell({
  kitStatus,
  coverage,
}: {
  kitStatus: KitStatusOverlay;
  coverage?: { required: number; allocated: number; shortBy: number; isShort: boolean };
}) {
  const overBy = coverage ? Math.max(0, coverage.allocated - coverage.required) : 0;
  return (
    <div className="flex flex-col gap-0.5 leading-tight">
      <div className="flex items-center gap-1">
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Kit</span>
        <KitCell status={kitStatus} />
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Alloc</span>
        {!coverage ? (
          <span className="text-[10px] text-muted-foreground">—</span>
        ) : coverage.isShort ? (
          <span className="text-[10px] text-warning">
            short by {coverage.shortBy} · {coverage.allocated}/{coverage.required}
          </span>
        ) : overBy > 0 ? (
          <span className="text-[10px] text-warning">
            over by {overBy} · {coverage.allocated}/{coverage.required}
          </span>
        ) : (
          <span className="text-[10px] text-success">
            {coverage.allocated}/{coverage.required}
          </span>
        )}
      </div>
    </div>
  );
}

function bannerFor(
  ok: boolean,
  missing: ("spec" | "po" | "kit")[],
  summary: string,
): { label: string; icon: JSX.Element; badgeClass: string; bannerClass: string; detail: string } {
  if (ok) {
    return {
      label: "Aligned",
      icon: <CheckCircle2 className="h-3 w-3" />,
      badgeClass: "text-success border-success/30",
      bannerClass: "border-success/30 bg-success/5 text-success",
      detail: "Spec, pushed Purchase Order, and Material Kit all match. Materials are aligned for install.",
    };
  }
  if (missing.includes("po")) {
    return {
      label: "Missing PO",
      icon: <Circle className="h-3 w-3" />,
      badgeClass: "text-muted-foreground border-border",
      bannerClass: "border-border bg-muted/20 text-muted-foreground",
      detail: "No pushed Purchase Order on file. Order has not been placed yet.",
    };
  }
  if (missing.includes("kit")) {
    return {
      label: "Missing Kit",
      icon: <Circle className="h-3 w-3" />,
      badgeClass: "text-muted-foreground border-border",
      bannerClass: "border-border bg-muted/20 text-muted-foreground",
      detail: "Material Kit has not been generated from the bound spec.",
    };
  }
  if (missing.includes("spec")) {
    return {
      label: "Spec not bound",
      icon: <Circle className="h-3 w-3" />,
      badgeClass: "text-muted-foreground border-border",
      bannerClass: "border-border bg-muted/20 text-muted-foreground",
      detail: "No JobSpec is bound to this job, so alignment cannot be enforced.",
    };
  }
  return {
    label: "Out of sync",
    icon: <AlertTriangle className="h-3 w-3" />,
    badgeClass: "text-warning border-warning/30",
    bannerClass: "border-warning/30 bg-warning/5 text-warning",
    detail: summary,
  };
}
